/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import { Dropdown as BasicDropdown } from 'ember-basic-dropdown/components/basic-dropdown';
import { FlyKeyboardEvent } from '@customerio/fly-components/types/fly-components/events';
import AssetModel from 'cio/models/asset';
import { FolderPath } from 'cio/components/asset-library';

interface AssetLibraryToolbarArgs {
  assets: AssetModel[];

  page: number;
  itemsPerPage: number;
  totalRecords: number;
  onPageChange: (page: number) => unknown;

  checkedAssetIds?: string[];
  onClearCheckedAssets?: () => unknown;
  onDeleteCheckedAssets?: () => unknown;
  onCheckAllAssets?: () => unknown;

  currentFolder?: FolderPath;
  folderPaths?: FolderPath[];
  onFolderPathNavigation?: (folderId: string) => unknown;
  onDropAssetOnFolder?: (asset: AssetModel | null) => unknown;

  viewToggleValue?: 'grid' | 'list';
  onToggleView?: (toggle: string) => unknown;

  sortBy?: string;
  sortAsc?: boolean;
  onSortByChange?: (sortBy: string) => unknown;
  onSortDirectionChange?: (sortBy: string) => unknown;

  search?: string;
  onSearchChange?: (search: string) => unknown;
}

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'created_at', label: 'Created' },
  { value: 'size', label: 'Size' },
];

export default class AssetLibraryToolbar extends Component<AssetLibraryToolbarArgs> {
  @service store!: DS.Store;

  readonly sortOptions = SORT_OPTIONS;

  get cannotDeleteCheckedItems() {
    return this.args.assets.some((a) => a.isDir && a.size > 0 && this.args.checkedAssetIds?.includes(a.id));
  }

  get checkedAssetCount() {
    return this.args.checkedAssetIds?.length ?? 0;
  }

  get displayCheckControls() {
    return this.checkedAssetCount > 0;
  }

  get displaySortControls() {
    return !this.displayCheckControls && this.args.sortBy != null && this.args.sortAsc != null;
  }

  get displaySearchField() {
    return this.args.search != null && this.args.onSearchChange != null && !this.displayCheckControls;
  }

  get displayPagination() {
    return this.args.totalRecords > 0 || this.args.search?.length;
  }

  @computed('args.sortBy')
  get sortLabel() {
    return this.sortOptions.find((o) => o.value === this.args.sortBy)?.label ?? '';
  }

  @action
  onSortByChange(sortBy: string, dd: BasicDropdown) {
    if (this.args.onSortByChange != null) {
      this.args.onSortByChange(sortBy);
    }
    dd.actions.close();
  }

  @action
  onSortDirectionChange(sortBy: string) {
    if (this.args.onSortDirectionChange != null) {
      this.args.onSortDirectionChange(sortBy);
    }
  }

  @action
  onSearchChange(event: FlyKeyboardEvent<HTMLInputElement>) {
    let search = event.target.value;
    if (this.args.onSearchChange != null) {
      this.args.onSearchChange(search);
    }
  }

  @action
  onFolderPathNavigation(folderId: string) {
    this.args.onFolderPathNavigation?.(folderId);
  }

  @action
  onDropAssetOnFolder(path: FolderPath) {
    if (this.args.onDropAssetOnFolder == null) {
      return;
    }

    let asset = path.id == null ? null : this.store.peekRecord('asset', path.id);
    this.args.onDropAssetOnFolder?.(asset);
  }
}
